import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CringeShell from './CringeShell';
import { GameHead, TopBar, useSaveScore } from './common';
import { useGameTimer } from './hooks';

// js/wordpuzzle.js
const WORDS = ['STAR', 'LIGHTS', 'GINGERBREAD', 'BOWS', 'CANDY', 'GIFTS'];
const GRID_SIZE = 14;
const DECORATIONS = { STAR: 'star', LIGHTS: 'lights', GINGERBREAD: 'cookie', BOWS: 'bows', CANDY: 'candy', GIFTS: 'gifts' };

// Words go right, down or down-right, never overlapping; the rest is random letters.
function buildGrid() {
  for (let tries = 0; tries < 50; tries++) {
    const grid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(''));
    const allPlaced = WORDS.every((word) => {
      for (let attempt = 0; attempt < 100; attempt++) {
        const dir = Math.floor(Math.random() * 3); // 0 horizontal, 1 vertical, 2 diagonal
        const row = Math.floor(Math.random() * GRID_SIZE);
        const col = Math.floor(Math.random() * GRID_SIZE);
        const dr = dir === 0 ? 0 : 1;
        const dc = dir === 1 ? 0 : 1;
        if (row + dr * (word.length - 1) >= GRID_SIZE || col + dc * (word.length - 1) >= GRID_SIZE) continue;
        let free = true;
        for (let i = 0; i < word.length && free; i++) free = grid[row + dr * i][col + dc * i] === '';
        if (!free) continue;
        for (let i = 0; i < word.length; i++) grid[row + dr * i][col + dc * i] = word[i];
        return true;
      }
      return false;
    });
    if (allPlaced) {
      return grid.map((r) => r.map((c) => c || String.fromCharCode(65 + Math.floor(Math.random() * 26))));
    }
  }
  throw new Error('Could not build the word grid');
}

const key = (r, c) => `${r}-${c}`;

/** Cells from start to end if the drag is right, down or down-right in a straight line (highlightPath). */
function pathBetween(start, end) {
  const dx = end.c - start.c;
  const dy = end.r - start.r;
  if (dx < 0 || dy < 0 || !(dx === dy || dx === 0 || dy === 0)) return [];
  const steps = Math.max(dx, dy);
  const sx = dx === 0 ? 0 : 1;
  const sy = dy === 0 ? 0 : 1;
  return Array.from({ length: steps + 1 }, (_, i) => ({ r: start.r + i * sy, c: start.c + i * sx }));
}

function Stage5Game() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const game = useSelector((s) => s.cringe.game);
  const timer = useGameTimer(true, game?.time);
  const save = useSaveScore();
  const [grid] = useState(buildGrid);
  const [selected, setSelected] = useState(() => new Set()); // cells of found words
  const [path, setPath] = useState([]); // cells being dragged over
  const [found, setFound] = useState([]);
  const [points, setPoints] = useState(game?.totalScore || 0);
  const [busy, setBusy] = useState(false);
  const start = useRef(null);
  const tableRef = useRef(null);

  const cellAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    if (!el || el.tagName !== 'TD' || !tableRef.current?.contains(el)) return null;
    return { r: Number(el.dataset.row), c: Number(el.dataset.col) };
  };

  const begin = (x, y) => {
    const cell = cellAt(x, y);
    if (!cell || selected.has(key(cell.r, cell.c))) return;
    start.current = cell;
    setPath([cell]);
  };

  const extend = (x, y) => {
    if (!start.current) return;
    const cell = cellAt(x, y);
    if (!cell || selected.has(key(cell.r, cell.c))) return;
    setPath(pathBetween(start.current, cell).filter((p) => !selected.has(key(p.r, p.c))));
  };

  const end = () => {
    if (!start.current) return;
    start.current = null;
    const word = path.map((p) => grid[p.r][p.c]).join('');
    const reversed = word.split('').reverse().join('');
    const match = WORDS.includes(word) ? word : WORDS.includes(reversed) ? reversed : null;
    if (match && !found.includes(match)) {
      setSelected((s) => new Set([...s, ...path.map((p) => key(p.r, p.c))]));
      setFound((f) => [...f, match]);
      setPoints((p) => p + 1);
    }
    setPath([]);
  };

  // Mouse-up / touch-end anywhere ends the drag (wordpuzzle.js listened on the document).
  const endRef = useRef(end);
  endRef.current = end;
  useEffect(() => {
    const up = () => endRef.current();
    document.addEventListener('mouseup', up);
    document.addEventListener('touchend', up);
    return () => {
      document.removeEventListener('mouseup', up);
      document.removeEventListener('touchend', up);
    };
  }, []);

  // Touch moves are handled on the table without scrolling the page.
  useEffect(() => {
    const table = tableRef.current;
    const onStart = (e) => {
      e.preventDefault();
      begin(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onMove = (e) => {
      e.preventDefault();
      extend(e.touches[0].clientX, e.touches[0].clientY);
    };
    table.addEventListener('touchstart', onStart, { passive: false });
    table.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      table.removeEventListener('touchstart', onStart);
      table.removeEventListener('touchmove', onMove);
    };
  });

  const onNext = async () => {
    if (busy) return;
    setBusy(true);
    const state = await save(5, points, timer.current());
    if (state) navigate({ pathname: '/thankyou', search });
    else setBusy(false);
  };

  const inPath = new Set(path.map((p) => key(p.r, p.c)));

  return (
    <>
      <TopBar time={timer.text} points={points} />
      <div className="cr-stage-body cr-stage5-body">
        <GameHead>
          You've done great so far! But Cringe didn't just steal the decorations—he also mixed them up into a chaotic
          pile! To bring back the holiday spirit, you need to arrange the decorations and gifts in their correct places
          on the holiday tree.
        </GameHead>

        <div className="cr-stage5-layout">
          <div className="cr-stage5-col cr-stage5-gridcol">
            <table className="cr-wordgrid" ref={tableRef}>
              <tbody>
                {grid.map((row, r) => (
                  <tr key={r}>
                    {row.map((letter, c) => {
                      const k = key(r, c);
                      const cls = selected.has(k) ? 'selected' : inPath.has(k) ? 'highlighted' : '';
                      return (
                        <td
                          key={c}
                          data-row={r}
                          data-col={c}
                          className={cls}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            begin(e.clientX, e.clientY);
                          }}
                          onMouseOver={(e) => extend(e.clientX, e.clientY)}
                        >
                          {letter}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cr-stage5-col cr-stage5-words">
            {found.map((w) => (
              <input key={w} type="text" readOnly value={w} />
            ))}
            {found.length >= 1 && (
              <button type="button" className="cr-img-btn cr-next-btn" onClick={onNext} disabled={busy}>
                <img src="/cringe/img/next.png" alt="Next" />
              </button>
            )}
          </div>

          <div className="cr-stage5-col cr-stage5-treecol">
            <div className="cr-tree">
              <img src="/cringe/img/stage5/tree.png" className="cr-tree-img" alt="Holiday tree" />
              {WORDS.map((w) => (
                <img
                  key={w}
                  src={`/cringe/img/stage5/${DECORATIONS[w]}.png`}
                  className={`cr-deco cr-deco-${w} ${found.includes(w) ? 'show' : ''}`}
                  alt={w}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// stage5.php: find the six decorations in the word search to put them back on the tree — 1 point each.
export default function CringeStage5() {
  return (
    <CringeShell bg="stage5/stage5_desk.png" bgMob="bg_mob.png" stage={5} className="cr-stage cr-stage5">
      <Stage5Game />
    </CringeShell>
  );
}
